import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingBag, Trash2 } from "lucide-react";
import { getCart, removeFromCart, updateQty, clearCart } from "@/lib/cart";
import SafeImage from "@/components/SafeImage";

export default function Cart() {
  const [cart, setCartState] = useState({ items: [] });
  const [err, setErr] = useState("");
  const navigate = useNavigate();
  useEffect(() => {
    setCartState(getCart());
    const sync = () => setCartState(getCart());
    window.addEventListener("cart:updated", sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener("cart:updated", sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const remove = (slug) => { removeFromCart(slug); setCartState(getCart()); };
  const clear = () => { clearCart(); setCartState(getCart()); };
  const setQty = (slug, qty) => { updateQty(slug, qty); setCartState(getCart()); };

  const subtotal = cart.items.reduce((acc, i) => acc + i.price_cents * i.qty, 0);

  // Phase 16.5 — Cart no longer launches Stripe directly. It hands off to
  // /checkout which captures shipping address + computes 8% shipping + 3% tax,
  // THEN creates the Stripe session with the locked totals.
  const proceedToAddress = () => {
    if (cart.items.length === 0) { setErr("Cart is empty"); return; }
    navigate("/checkout");
  };

  return (
    <div data-testid="cart" className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 py-12">
      <header className="mb-8">
        <div className="eyebrow flex items-center gap-2"><ShoppingBag className="h-3 w-3" /> Cart</div>
        <h1 className="font-anton text-3xl sm:text-4xl uppercase mt-2">Your bag</h1>
        <p className="text-lifted-dim mt-2 text-sm">Secure checkout powered by Shopify. Card, Apple Pay, and Shop Pay supported.</p>
      </header>

      {cart.items.length === 0 ? (
        <div className="glass-panel p-12 text-center" data-testid="cart-empty">
          <div className="font-anton uppercase text-xl mb-2">Bag is empty</div>
          <Link to="/collections/lifted-gear" className="btn-lifted mt-4 inline-flex">Browse Lifted Gear</Link>
        </div>
      ) : (
        <>
          <div className="glass-panel overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[0.7rem] tracking-[0.22em] uppercase text-lifted-mute font-bebas border-b border-white/5">
                  <th className="px-4 py-3" colSpan={2}>Item</th><th>Qty</th><th>Price</th><th></th>
                </tr>
              </thead>
              <tbody>
                {cart.items.map((i) => {
                  // Phase 16.5 — cart items may have been written with `media[]`
                  // OR a flat `image` field depending on the entry path.
                  // Resolve defensively so a missing key cannot crash <Cart>.
                  const thumb = (Array.isArray(i.media) && i.media[0])
                                || i.image_url || i.image || "";
                  const key = i.cart_key || i.slug;
                  return (
                  <tr key={key} data-testid={`cart-item-${key}`} className="border-b border-white/5">
                    <td className="px-4 py-3 w-20">
                      {thumb ? (
                        <SafeImage src={thumb} alt="" className="h-16 w-16 rounded object-cover" />
                      ) : (
                        <div className="h-16 w-16 rounded bg-white/5 grid place-items-center text-[0.55rem] font-bebas tracking-[0.18em] text-lifted-dim">
                          NO IMG
                        </div>
                      )}
                    </td>
                    <td>
                      <Link to={`/products/${i.slug}`} className="text-lifted-text hover:text-lifted-green">{i.title}</Link>
                      {i.variant_title && (
                        <div className="text-[11px] text-lifted-green mt-0.5 font-bebas tracking-[0.18em] uppercase" data-testid={`cart-variant-${key}`}>
                          Variant: {i.variant_title}
                        </div>
                      )}
                      <div className="text-xs text-lifted-mute mt-0.5">${(i.price_cents / 100).toFixed(2)} ea</div>
                    </td>
                    <td>
                      <input type="number" min={1} value={i.qty} onChange={(e) => setQty(key, parseInt(e.target.value || "1"))}
                             data-testid={`cart-qty-${key}`} className="input-lifted text-sm w-20 text-center" />
                    </td>
                    <td className="text-lifted-text">${((i.price_cents * i.qty) / 100).toFixed(2)}</td>
                    <td>
                      <button onClick={() => remove(key)} className="btn-ghost !p-2 hover:!text-rose-300" data-testid={`cart-remove-${key}`}>
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </td>
                  </tr>
                );
                })}
              </tbody>
            </table>
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
            <button onClick={clear} className="btn-ghost !text-[0.65rem]" data-testid="cart-clear">Clear cart</button>
            <div className="text-right">
              <div className="text-xs text-lifted-mute font-bebas tracking-[0.18em] uppercase">Subtotal</div>
              <div className="font-anton text-3xl text-lifted-green">${(subtotal / 100).toFixed(2)} USD</div>
              <div className="text-xs text-lifted-mute mt-1">Shipping calculated at checkout</div>
            </div>
          </div>

          {/* Iter89 — Shopify-hosted checkout (Square retained as rollback) */}
          <div className="mt-6 glass-panel p-5 border-lifted-green/30" data-testid="cart-handoff">
            <div className="text-sm text-lifted-text mb-1 font-bebas tracking-[0.18em] uppercase">
              Secure checkout · Shopify
            </div>
            <div className="text-xs text-lifted-mute mb-3">
              You'll continue to a Shopify-powered checkout. Your bag stays
              attached to your Lifted account. Card, Apple Pay, and Shop Pay supported.
            </div>
            {err && <div className="text-xs text-rose-300 mb-2" data-testid="cart-checkout-error">{err}</div>}
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={proceedToAddress}
                disabled={cart.items.length === 0}
                className="btn-lifted disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center gap-2"
                data-testid="cart-checkout"
              >
                Continue to checkout
              </button>
              <Link to="/shop" className="btn-ghost" data-testid="cart-continue-shopping">
                Continue shopping
              </Link>
              {cart.items.slice(0, 3).map((i) => (
                <Link
                  key={i.slug}
                  to={`/products/${i.slug}`}
                  className="btn-ghost !text-[0.65rem]"
                  data-testid={`cart-view-${i.slug}`}
                >
                  View "{i.title.slice(0, 20)}…"
                </Link>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
